/* eslint-disable sort-keys */
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, SquareCheck, Upload } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { getStatusBadge } from "./courseDetailUtils";
import { ActionCell, BoxColumnCell, FilterBox, TypographyCell } from "./SignupSummary.styles";
import { downloadBlobFile } from "../../utils/general";


// interface CourseItem {
//   title: string;
//   type?: string;
//   provider: string;
//   startDate?: string; // si presume come ISO string (es. "2025-05-02")
//   endDate?: string;
//   status: "APPROVED" | "REJECTED" | "PENDING";
// }

type SortKey = "status" | "type" | "startDate" | null;
type SortDirection = "asc" | "desc";

interface CoursesTableProps {
  courses: CourseItem[];
  selectedCourse: CourseItem | null;
  onSelect: (course: CourseItem) => void;
  filter: string;
  paginatedCourses: CourseItem[];
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSortChange: (key: SortKey) => void;
  onUploadCertificate: (course: CourseItem, file: File) => void;
  onMarkAsCompleted: (course: CourseItem) => void;
  onMarkConsuntivazioneCompleted: (course: CourseItem) => void;
}

export const CoursesTable: React.FC<CoursesTableProps> = ({
  courses,
  onSelect,
  selectedCourse,
  filter: _filter,
  paginatedCourses,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  sortKey,
  sortDirection,
  onSortChange,
  onUploadCertificate,
  onMarkAsCompleted,
  onMarkConsuntivazioneCompleted
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const uploadCourseRef = React.useRef<CourseItem | null>(null);

  const handleDownloadCertificate = async (url: string) => {
    try {
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const blob = await response.blob();
      downloadBlobFile({ fileName: "certificato.pdf", blobFile: blob });
    } catch {
      window.open(url, "_blank");
    }
  };

  const formatDateIt = (dateString?: string) => {
    return dateString
      ? new Date(dateString).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
      : "-";
  };

  const totalPages = Math.ceil(courses.length / itemsPerPage);
  const rangeStart = (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, courses.length);


  return (
    <Box
      sx={ {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        mt: 4,
        minWidth: 0
      } }
    >
      { /* Header delle colonne */ }
      { !isXs && (
        <Box
          sx={ {
            alignItems: "center",
            color: theme.customColors.backgroundPrimaryCta,
            display: "grid",
            fontSize: "0.75rem",
            fontWeight: 600,
            gap: "1.25rem",
            gridTemplateColumns:
              "minmax(0, 1.8fr) minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr)",
            userSelect: "none"
          } }
        >
          <Box>{ t("title_training") }</Box>
          <FilterBox
            onClick={ () => onSortChange("status") }
          >
            { t("all_status") }
            { sortKey === "status"
              ? (sortDirection === "asc" ? <ArrowUp size={ 14 } /> : <ArrowDown size={ 14 } />)
              : <ArrowDown size={ 14 } style={ { opacity: 0.4 } } />
            }
          </FilterBox>
          <FilterBox
            onClick={ () => onSortChange("type") }
          >
            { t("type_training") }
            { sortKey === "type"
              ? (sortDirection === "asc" ? <ArrowUp size={ 14 } /> : <ArrowDown size={ 14 } />)
              : <ArrowDown size={ 14 } style={ { opacity: 0.4 } } />
            }
          </FilterBox>
          <FilterBox
            onClick={ () => onSortChange("startDate") }
          >
            { t("date_start_training") }
            { sortKey === "startDate"
              ? (sortDirection === "asc" ? <ArrowUp size={ 14 } /> : <ArrowDown size={ 14 } />)
              : <ArrowDown size={ 14 } style={ { opacity: 0.4 } } />
            }
          </FilterBox>
          <Box>{ t("date_end_training") }</Box>
          <Box>{ t("actions") }</Box>
        </Box>
      ) }

      { /* Dati */ }
      <Box>
        { paginatedCourses.map((course, idx) => (
          <Box
            key={ idx }
            onClick={ () => onSelect(course) }
            sx={ {
              backgroundColor: selectedCourse === course
                ? theme.customColors.backgroundPrimaryControlHover
                : "transparent",
              borderBottom: `1px solid ${theme.customColors.border}`,
              cursor: "pointer",
              display: "grid",
              gap: "1.25rem",
              gridTemplateColumns: isXs
                ? "1fr"
                : "minmax(0, 1.8fr) minmax(0, 2fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr)",
              maxWidth: "100%",
              p: "1rem 0",
              transition: "background-color .2s",
              width: "100%"
            } }
          >
            { isXs ? (
              <>
                <Box>
                  <strong>{ t("title_training") }: </strong>
                  { course.title }
                </Box>
                <Box>
                  <strong>{ t("all_status") }: </strong>
                  { getStatusBadge(course.status, theme, t) }
                </Box>
                <Box>
                  <strong>{ t("type_training") }: </strong>
                  { course.type  }
                </Box>
                <Box>
                  <strong>{ t("date_start_training") }: </strong>
                  { formatDateIt(course.startDate) }
                </Box>
                <Box>
                  <strong>{ t("date_end_training") }: </strong>
                  { formatDateIt(course.endDate) }
                </Box>
              </>
            ) : (
              <>
                { /* Titolo */ }
                <BoxColumnCell
                  sx={ {
                    textDecoration: "underline",
                    textUnderlineOffset: "0.2rem",
                    textDecorationColor: theme.customColors.exploreCardText
                  } }
                >
                  { course.title }
                </BoxColumnCell>

                { /* Stato */ }
                <BoxColumnCell
                  sx={ {
                    alignItems: "center",
                    display: "flex" ,
                    width: "max-content"
                  } }
                >
                  { getStatusBadge(course.status, theme, t) }
                </BoxColumnCell>

                { /* Tipo */ }
                <BoxColumnCell>
                  { course.type  }
                </BoxColumnCell>

                { /* Inizio */ }
                <BoxColumnCell>
                  { formatDateIt(course.startDate) }
                </BoxColumnCell>

                { /* Fine */ }
                <BoxColumnCell>
                  { formatDateIt(course.endDate) }
                </BoxColumnCell>

                { /* Azioni */ }
                <BoxColumnCell
                >
                  { course.source === "internal" ? (
                    /* Interna: solo download se pdfKey presente */
                    course.pdfKey && (
                      <ActionCell
                        onClick={ (e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          handleDownloadCertificate(course.pdfKey as string);
                        } }
                        sx={ {
                          "& svg": {
                            minWidth:"0.875rem"
                          }
                        } }
                      >
                        <Download size={ 14 } />
                        { t("download_certificate") }
                      </ActionCell>
                    )
                  ) : (
                    /* Formazione esterna */
                    course.pdfKey ? (
                      <ActionCell
                        onClick={ (e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          handleDownloadCertificate(course.pdfKey as string);
                        } }
                        sx={ {
                          "& svg": {
                            minWidth:"0.875rem"
                          }
                        } }
                      >
                        <Download size={ 14 } />

                        <TypographyCell>
                          { t("download_certificate") }
                        </TypographyCell>
                      </ActionCell>
                    ) : course.status === "PENDING" || course.status === "REJECTED" ? (
                      null
                    ) : course.type === "Iscrizione" && course.status === "APPROVED" ? (
                      <Box
                        sx={ {
                          alignItems: "flex-start",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem"
                        } }
                      >
                        <ActionCell
                          onClick={ (e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            uploadCourseRef.current = course;
                            fileInputRef.current?.click();
                          } }
                          sx={ {
                            "& svg": {
                              minWidth:"0.875rem"
                            }
                          } }
                        >
                          <Upload size={ "0.875rem" } />
                          <TypographyCell>
                            { t("file_field") }
                          </TypographyCell>
                        </ActionCell>
                        <ActionCell
                          onClick={ (e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            onMarkConsuntivazioneCompleted(course);
                          } }
                          sx={ {
                            "& svg": {
                              minWidth:"0.875rem"
                            }
                          } }
                        >
                          <SquareCheck size={ "0.875rem" } />
                          <TypographyCell>
                            { t("comleted_button") }
                          </TypographyCell>
                        </ActionCell>
                      </Box>
                    ) : course.startDate && !course.endDate ? (
                      <ActionCell
                        onClick={ (e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          onMarkAsCompleted(course);
                        } }
                        sx={ {
                          "& svg": {
                            minWidth:"0.875rem"
                          }
                        } }
                      >
                        <SquareCheck size={ "0.875rem" } />
                        <TypographyCell>
                          { t("comleted_button") }
                        </TypographyCell>
                      </ActionCell>
                    ) : course.type === "Consuntivazione" && course.status === "APPROVED" ? null : (
                      <ActionCell
                        onClick={ (e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          uploadCourseRef.current = course;
                          fileInputRef.current?.click();
                        } }
                        sx={ {
                          "& svg": {
                            minWidth:"0.875rem"
                          }
                        } }
                      >
                        <Upload size={ "0.875rem" } />
                        <TypographyCell>
                          { t("file_field") }
                        </TypographyCell>
                      </ActionCell>
                    )
                  ) }
                </BoxColumnCell>
              </>
            ) }
          </Box>
        )) }
      </Box>

      { /* Paginazione */ }
      <Box
        sx={ {
          alignItems: "center",
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          mt: "auto",
          p: "1rem 0rem 0rem"
        } }
      >
        <Typography
          variant="body2"
          sx={ { color: theme.customColors.textPrimary, mr: 1 } }
        >
          { `${rangeStart}-${rangeEnd} di ${courses.length}` }
        </Typography>
        <IconButton
          onClick={ () => currentPage > 1 && setCurrentPage(currentPage - 1) }
          size="small"
          sx={ {
            color: currentPage === 1
              ? theme.customColors.textPrimary
              : theme.customColors.backgroundPrimaryCta,
            cursor: currentPage === 1 ? "default" : "pointer",
            opacity: currentPage === 1 ? 0.3 : 1
          } }
        >
          <ChevronLeft size={ 20 } />
        </IconButton>
        <IconButton
          onClick={ () => currentPage < totalPages && setCurrentPage(currentPage + 1) }
          size="small"
          sx={ {
            color: currentPage === totalPages
              ? theme.customColors.textPrimary
              : theme.customColors.backgroundPrimaryCta,
            cursor: currentPage === totalPages ? "default" : "pointer",
            opacity: currentPage === totalPages ? 0.3 : 1
          } }
        >
          <ChevronRight size={ 20 } />
        </IconButton>
      </Box>

      { /* Input file nascosto per upload certificato */ }
      <input
        ref={ fileInputRef }
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={ { display: "none" } }
        onChange={ (e) => {
          const file = e.target.files?.[0];
          if (file && uploadCourseRef.current) {
            onUploadCertificate(uploadCourseRef.current, file);
            uploadCourseRef.current = null;
          }
          e.target.value = "";
        } }
      />
    </Box>
  );
};
